"use client";

import {
  Button,
  cn,
  HR,
  Icon,
  ModalContent,
  ModalFooter,
  ModalHeader,
  PortalModal,
  usePortalContext,
} from "@cloai/uikit";
import { motion } from "framer-motion";
import { map } from "lodash";
import { useLocale, useTranslations } from "next-intl";
import { memo, useCallback, useMemo } from "react";

import {
  PhotoSettings,
  useGTMContext,
  useUndressContext,
  useUndressSettingsContext,
} from "@/providers";
import { getLocalizedName } from "@/utils/getLocalizedName";

import { ItemCard } from "./components/ItemCard";
import { STYLES } from "./styles";
import { MOTION_VARIANTS } from "./vars";
import { GE } from ".constants/gtmEvents";
import { PORTALS } from ".constants/portals";

export const CostumesPortal = memo(() => {
  const t = useTranslations("UserFlowModal");
  const {
    clickFileId,
    clickSettings,
    clickUndressingFile,
    initialSettings,
    onReUseFaceObj,
    reUseFaceObj,
    updatePhotoSetting,
  } = useUndressContext();
  const { costumes } = useUndressSettingsContext();
  const { closePortal, openPortal } = usePortalContext();
  const { sendGTM } = useGTMContext();

  const locale = useLocale();

  const { pose } = clickSettings;

  const isCanSkip = useMemo(
    () => pose !== initialSettings.pose && !clickUndressingFile,
    [initialSettings.pose, pose, clickUndressingFile],
  );

  const popupTitle = isCanSkip
    ? t("titles.nowChooseCostume")
    : t("titles.chooseCostume");

  const handleNavigation = useCallback(() => {
    closePortal();

    if (pose === initialSettings.pose && !clickUndressingFile) {
      return openPortal(PORTALS.USER_FLOW_POSES);
    }
    if (reUseFaceObj) {
      return onReUseFaceObj();
    }
    if (!clickUndressingFile) {
      return openPortal(PORTALS.USER_FLOW_UPLOAD);
    }
  }, [
    closePortal,
    pose,
    initialSettings.pose,
    clickUndressingFile,
    reUseFaceObj,
    openPortal,
    onReUseFaceObj,
  ]);

  const handleClick = useCallback(
    (itemType: keyof PhotoSettings, itemId: string) => () => {
      updatePhotoSetting(clickFileId, itemType, itemId);
      handleNavigation();
    },
    [clickFileId, handleNavigation, updatePhotoSetting],
  );

  const handleSkipCostume = useCallback(() => {
    sendGTM({ event: GE.FLOW_START_CLICK_SKIP_COSTUME });
    handleNavigation();
  }, [handleNavigation, sendGTM]);

  return (
    <PortalModal
      portalId={PORTALS.USER_FLOW_COSTUMES}
      style={STYLES.portal.variants.wide}
    >
      <ModalHeader>{popupTitle}</ModalHeader>

      <ModalContent className="@container">
        <motion.div
          variants={MOTION_VARIANTS.container}
          initial="hidden"
          animate="visible"
          role="grid"
          aria-label="Costumes grid"
          className={cn(STYLES.grid.variants.default, STYLES.grid.animate)}
        >
          {map(costumes, (item, Index) => (
            <motion.div
              key={item.id}
              variants={MOTION_VARIANTS.item}
              custom={Index}
              role="gridcell"
              aria-label={getLocalizedName(item, locale)}
            >
              <ItemCard
                item={item}
                index={Index}
                onClick={handleClick("costume", item.id)}
                variant="portrait"
              />
            </motion.div>
          ))}
        </motion.div>
      </ModalContent>

      {isCanSkip && (
        <ModalFooter>
          <HR text={t("or")} />

          <Button
            startContent={<Icon icon="double_arrow" />}
            wide
            centerText
            className="mx-auto max-w-110"
            onClick={handleSkipCostume}
          >
            {t("buttons.skipCostume")}
          </Button>
        </ModalFooter>
      )}
    </PortalModal>
  );
});

CostumesPortal.displayName = "CostumesPortal";
